'use client';

import { FC, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Autocomplete, Box, Typography, CircularProgress, Alert, Button, TextField } from '@mui/material';
import { StandardDialog } from '@/components/common/StandardDialog';
import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useDialogStore } from '@/stores/dialogStore';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import type { IBackend } from '@/api/BackendApi';
import { StatusDto, type BulkSampleDto, type InstrumentRunDto, type SampleActivityDto } from '@/api/ApiClient';
import Recipe from '@/components/recipe/Recipe';
import { mapRecipeStateToDto, mapRecipeDtoToState } from '@/lib/types/recipeMapper';
import { useRecipeEditor } from '@/components/recipe/hooks/useRecipeEditor';
import type { RecipeState } from '@/components/recipe';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { transformOptions } from '@/lib/utils/optionUtils';
import { filterAutocompleteOptionsByMatchRank } from '@/lib/utils/autocompleteMatchRank';
import { getPreparationRecipeViewHint, canEditPreparationRecipe } from '@/lib/utils/bulkPreparationRecipeDialog';
import { shouldSyncActivityRuntimeFromRecipe } from '@/lib/utils/recipeRuntime';
import {
  useRecipeActivityRuntime,
  useRecipeRuntimeCalculatedDelegate,
} from '@/components/recipe/hooks/useRecipeActivityRuntime';
import { RecipeActivityRuntimeField } from '@/components/recipe/RecipeActivityRuntimeField';
import { useAuthStore } from '@/stores/authStore';
import { useBulkFilterStore } from '@/stores/bulkFilterStore';
import { MaterialCreateAddButton } from '@/components/material/MaterialCreateAddButton';
import { MaterialCreateDialog } from '@/components/material/MaterialCreateDialog';
import { useCreateMaterial } from '@/hooks/useCreateMaterial';

/**
 * Bulk Recipe Editor Dialog
 *
 * Outer shell loads the activity; view mode renders recipe data directly; edit mode mounts the full editor.
 */
const OPTION_KEYS: OptionKey[] = ['synthesisTechniques', 'synthesisInstruments'];
const MATERIAL_OPTION_KEYS: OptionKey[] = ['materials'];

function getActivityMaterialId(activity: SampleActivityDto): number | undefined {
  const material = activity.materials?.find((m) => m.id != null);
  return material?.id;
}

function buildPreparationActivityInfoLine(
  activity: SampleActivityDto,
  techniqueLabel: string,
  instrumentLabel: string,
  forceReadOnly: boolean | undefined,
  viewingSampleLabel: string | undefined,
  preparationOwnerLabel: string | undefined
): string {
  if (forceReadOnly && viewingSampleLabel && preparationOwnerLabel) {
    const parts = [`Viewing from ${viewingSampleLabel}`, `Preparation for ${preparationOwnerLabel}`];
    if (techniqueLabel) {
      parts.push(`Technique: ${techniqueLabel}`);
    }
    if (instrumentLabel) {
      parts.push(`Instrument: ${instrumentLabel}`);
    }
    return parts.join(' • ');
  }

  const parts: string[] = [`Sample: ${activity.sample?.sampleLabel || 'N/A'}`];
  if (techniqueLabel) {
    parts.push(`Technique: ${techniqueLabel}`);
  }
  if (instrumentLabel) {
    parts.push(`Instrument: ${instrumentLabel}`);
  }
  return parts.join(' • ');
}

type BulkRecipeActivityType = 'preparation' | 'synthesis' | 'compounding';

/** Synthesis/compounding growth activities carry instrument runtime (hours). */
function isInstrumentRunActivityType(activityType: BulkRecipeActivityType): boolean {
  return activityType === 'synthesis' || activityType === 'compounding';
}

function showBulkRecipeRuntimeField(activityType: BulkRecipeActivityType, instrument: string | undefined): boolean {
  return isInstrumentRunActivityType(activityType) && shouldSyncActivityRuntimeFromRecipe({ instrument });
}

async function loadBulkRecipeActivity(
  backend: IBackend,
  activityId: number,
  activityType: BulkRecipeActivityType
): Promise<SampleActivityDto | undefined> {
  if (activityType === 'preparation') {
    return backend.bulkSample.getPreparation(activityId);
  }
  if (activityType === 'synthesis') {
    const result = await backend.bulkSample.getSynthesis(activityId);
    return result.activity;
  }
  const result = await backend.bulkSample.getCompounding(activityId);
  return result.activity;
}

function getBulkRecipeDialogTitle(
  activityType: BulkRecipeActivityType,
  mode: 'view' | 'edit',
  startingMaterialNumber?: number
): string {
  const action = mode === 'view' ? 'View' : 'Edit';
  const typeLabels: Record<BulkRecipeActivityType, string> = {
    preparation: `${action} Preparation Recipe${startingMaterialNumber ? ` (StartingMaterial ${startingMaterialNumber})` : ''}`,
    synthesis: `${action} Synthesis Recipe`,
    compounding: `${action} Compounding Recipe`,
  };
  return typeLabels[activityType] ?? `${action} Recipe`;
}

function useActivityLabels(
  activity: SampleActivityDto,
  crossRow?: {
    forceReadOnly?: boolean;
    viewingSampleLabel?: string;
    preparationOwnerLabel?: string;
  }
) {
  const { options } = useSelectOptions(OPTION_KEYS);

  const techniqueLabel = useMemo(() => {
    if (!activity.technique) return '';
    return options.synthesisTechniques?.find((t) => t.id === activity.technique)?.name || activity.technique;
  }, [activity.technique, options.synthesisTechniques]);

  const instrumentLabel = useMemo(() => {
    if (!activity.instrument) return '';
    return options.synthesisInstruments?.find((i) => i.id === activity.instrument)?.name || activity.instrument;
  }, [activity.instrument, options.synthesisInstruments]);

  const activityInfoLine = useMemo(
    () =>
      buildPreparationActivityInfoLine(
        activity,
        techniqueLabel,
        instrumentLabel,
        crossRow?.forceReadOnly,
        crossRow?.viewingSampleLabel,
        crossRow?.preparationOwnerLabel
      ),
    [
      activity,
      techniqueLabel,
      instrumentLabel,
      crossRow?.forceReadOnly,
      crossRow?.viewingSampleLabel,
      crossRow?.preparationOwnerLabel,
    ]
  );

  return activityInfoLine;
}

type BulkRecipeEditorDialogInnerProps = {
  activity: SampleActivityDto;
  activityId: number;
  activityType: BulkRecipeActivityType;
  startingMaterialNumber?: number;
  startingMaterialSampleLabel?: string;
  viewingSampleLabel?: string;
  preparationOwnerRoles?: number[];
  forceReadOnly?: boolean;
  initialMode: 'view' | 'edit';
  closeBulkRecipeEditorDialog: (result?: InstrumentRunDto | SampleActivityDto | BulkSampleDto | null) => void;
};

/** Read-only recipe grid from activity DTO — no template fetch or recipe editor hook. */
const BulkRecipeViewPanel: FC<{
  activity: SampleActivityDto;
  activityType: BulkRecipeActivityType;
  activityInfoLine: string;
  startingMaterialNumber?: number;
  startingMaterialSampleLabel?: string;
  viewingSampleLabel?: string;
  preparationOwnerRoles?: number[];
  forceReadOnly?: boolean;
  onEdit: () => void;
  onClose: () => void;
  dialogTitle: string;
}> = ({
  activity,
  activityType,
  activityInfoLine,
  startingMaterialNumber,
  startingMaterialSampleLabel,
  viewingSampleLabel,
  preparationOwnerRoles,
  forceReadOnly,
  onEdit,
  onClose,
  dialogTitle,
}) => {
  const isAuthorized = useAuthStore((s) => s.isAuthorized);
  const bulkRolePrivileges = useBulkFilterStore((s) => s.bulkRolePrivileges);
  const { options } = useSelectOptions(MATERIAL_OPTION_KEYS);
  const materialOptions = useMemo(
    () => transformOptions(options.materials, (m) => !m.isSubstrate),
    [options.materials]
  );

  const viewRecipeData = useMemo(
    () => (activity.recipe != null ? mapRecipeDtoToState(activity.recipe) : undefined),
    [activity.recipe]
  );
  const isSubmitted = activity.status === StatusDto.Submitted;
  const preparationOwnerLabel = startingMaterialSampleLabel || activity.sample?.sampleLabel;

  const canEditOnOwnerRow = useMemo(
    () => canEditPreparationRecipe(activity.status, { isAuthorized, bulkRolePrivileges }, preparationOwnerRoles),
    [activity.status, isAuthorized, bulkRolePrivileges, preparationOwnerRoles]
  );

  const viewHint = useMemo(
    () =>
      getPreparationRecipeViewHint({
        hasRecipe: activity.recipe != null,
        forceReadOnly,
        viewingSampleLabel,
        preparationOwnerLabel,
        startingMaterialNumber,
        activityStatus: activity.status,
        canEditOnOwnerRow,
      }),
    [
      activity.recipe,
      activity.status,
      forceReadOnly,
      viewingSampleLabel,
      preparationOwnerLabel,
      startingMaterialNumber,
      canEditOnOwnerRow,
    ]
  );

  const materialLabel = useMemo(() => {
    if (activityType !== 'preparation') return null;
    const materialId = getActivityMaterialId(activity);
    if (materialId == null) return null;
    const fromActivity = activity.materials?.find((m) => m.id === materialId)?.name;
    if (fromActivity) return fromActivity;
    return materialOptions.find((o) => String(o.id) === String(materialId))?.name ?? String(materialId);
  }, [activity, activityType, materialOptions]);

  return (
    <StandardDialog
      open
      onClose={onClose}
      title={dialogTitle}
      maxWidth="lg"
      actions={<Button onClick={onClose}>Close</Button>}
    >
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {activityInfoLine}
          </Typography>
          {viewHint ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {viewHint}
            </Typography>
          ) : !forceReadOnly && !isSubmitted && canEditOnOwnerRow ? (
            <Button variant="outlined" size="small" onClick={onEdit}>
              Edit
            </Button>
          ) : null}
        </Box>

        {materialLabel ? (
          <Typography variant="body2" sx={{ mb: 1 }}>
            <Box component="span" sx={{ color: 'text.secondary' }}>
              Target material:{' '}
            </Box>
            <Box component="span" dangerouslySetInnerHTML={{ __html: materialLabel }} />
          </Typography>
        ) : null}

        {showBulkRecipeRuntimeField(activityType, activity.instrument) ? (
          <Typography variant="body2" sx={{ mb: 1 }}>
            <Box component="span" sx={{ color: 'text.secondary' }}>
              Runtime (h):{' '}
            </Box>
            <Box component="span">{activity.runtime != null ? activity.runtime : 'N/A'}</Box>
          </Typography>
        ) : null}

        {viewRecipeData ? (
          <Box sx={{ mt: 2, width: '100%' }}>
            <Recipe data={viewRecipeData} readonly={true} />
          </Box>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No recipe found.
            </Typography>
          </Box>
        )}
      </Box>
    </StandardDialog>
  );
};

/** Full recipe editor — templates, store, save. Mounted only in edit mode. */
const BulkRecipeEditPanel: FC<{
  activity: SampleActivityDto;
  activityId: number;
  activityType: BulkRecipeActivityType;
  activityInfoLine: string;
  startingMaterialNumber?: number;
  forceReadOnly?: boolean;
  dialogTitle: string;
  onClose: () => void;
  closeBulkRecipeEditorDialog: (result?: InstrumentRunDto | SampleActivityDto | BulkSampleDto | null) => void;
}> = ({
  activity,
  activityId,
  activityType,
  activityInfoLine,
  forceReadOnly,
  dialogTitle,
  onClose,
  closeBulkRecipeEditorDialog,
}) => {
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { canCreateMaterial } = useCreateMaterial();
  const { options } = useSelectOptions(MATERIAL_OPTION_KEYS);
  const materialOptions = useMemo(
    () => transformOptions(options.materials, (m) => !m.isSubstrate),
    [options.materials]
  );

  const [saving, setSaving] = useState(false);
  const [isMaterialDialogOpen, setIsMaterialDialogOpen] = useState(false);
  const [templateOutdated, setTemplateOutdated] = useState(false);
  const [updatingTemplate, setUpdatingTemplate] = useState(false);
  const templateCheckedForActivityRef = useRef<string | null>(null);

  const [initialMaterialId] = useState(() => getActivityMaterialId(activity));
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | undefined>(initialMaterialId);

  const [initialRecipeState, setInitialRecipeState] = useState<RecipeState | undefined>(() =>
    activity.recipe != null ? mapRecipeDtoToState(activity.recipe) : undefined
  );

  const showRuntimeField = showBulkRecipeRuntimeField(activityType, activity.instrument);
  const [initialRuntime] = useState(() => activity.runtime);
  const [runtime, setRuntime] = useState<number | undefined>(() => activity.runtime);
  const runtimeCalculatedDelegate = useRecipeRuntimeCalculatedDelegate();

  const recipeEditor = useRecipeEditor({
    activity: {
      type: activity.type,
      technique: activity.technique,
      instrument: activity.instrument,
      recipe: activity.recipe,
      recipeTemplate: activity.recipeTemplate,
      status: activity.status,
      sample: activity.sample,
    },
    isReadOnly: forceReadOnly === true ? true : undefined,
    autoLoadTemplates: true,
    onRuntimeCalculated: showRuntimeField ? runtimeCalculatedDelegate.delegate : undefined,
  });

  const runtimeField = useRecipeActivityRuntime({
    enabled: showRuntimeField,
    onRuntimeChange: setRuntime,
    runtimeChangedManuallyInitial: initialRecipeState?.runtimeChangedManually ?? false,
    recipeStore: recipeEditor.recipeStore,
    runtimeUnit: recipeEditor.fullRecipeTemplate?.runtimeUnit ?? recipeEditor.recipeTemplateDTO?.runtimeUnit,
  });
  runtimeCalculatedDelegate.bind(runtimeField.onRuntimeCalculated);

  const { setRecipeData, setFullRecipeTemplate, checkTemplateOutdated } = recipeEditor;

  /** Match legacy chooseMaterial: template.definesMaterial, or show if activity already has materials when template is missing. */
  const showMaterialSection = useMemo(() => {
    if (activityType !== 'preparation') return false;
    if (recipeEditor.fullRecipeTemplate != null) {
      return recipeEditor.fullRecipeTemplate.definesMaterial === true;
    }
    return (activity.materials?.filter((m) => m.id != null).length ?? 0) > 0;
  }, [activityType, activity.materials, recipeEditor.fullRecipeTemplate]);

  const selectedMaterialOption = useMemo(() => {
    if (selectedMaterialId == null) return null;
    return materialOptions.find((o) => String(o.id) === String(selectedMaterialId)) ?? null;
  }, [materialOptions, selectedMaterialId]);

  useEffect(() => {
    return () => {
      setRecipeData(undefined);
      setFullRecipeTemplate(null);
    };
  }, [setRecipeData, setFullRecipeTemplate]);

  useEffect(() => {
    const id = activity.id?.toString();
    const checkKey = id ?? '';
    const templateName = activity.recipe?.templateName || activity.recipeTemplate;

    if (!id || activity.recipe == null || !templateName) {
      return;
    }
    if (templateCheckedForActivityRef.current === checkKey) {
      return;
    }
    templateCheckedForActivityRef.current = checkKey;

    let cancelled = false;

    void (async () => {
      if (!activity.recipe?.templateHash) {
        if (!cancelled) {
          setTemplateOutdated(true);
        }
        return;
      }

      const outdated = await checkTemplateOutdated({
        activityType: activity.type,
        technique: activity.technique,
        sampleTechnique: activity.sample?.techniqueId,
        instrument: activity.instrument || undefined,
        templateName,
        storedHash: activity.recipe.templateHash,
      });
      if (!cancelled) {
        setTemplateOutdated(outdated);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activity, checkTemplateOutdated]);

  const handleSave = useCallback(async () => {
    if (!recipeEditor.recipeStore || recipeEditor.isReadOnly) return;

    setSaving(true);
    try {
      const recipeDto = mapRecipeStateToDto(recipeEditor.recipeStore.toState());
      const updatedActivity: SampleActivityDto = {
        ...activity,
        recipe: recipeDto,
        ...(showRuntimeField ? { runtime } : {}),
      };

      let saved: InstrumentRunDto | SampleActivityDto | BulkSampleDto;

      if (activityType === 'preparation') {
        saved = await backend.bulkSample.updatePreparation(activityId, {
          id: activityId,
          sampleId: activity.sample?.id,
          recipe: recipeDto,
          ...(showMaterialSection && selectedMaterialId != null ? { materialId: selectedMaterialId } : {}),
        });
      } else if (activityType === 'synthesis') {
        saved = await backend.bulkSample.updateSynthesis(activityId, {
          activityId,
          activity: updatedActivity,
          technique: activity.technique,
          instrument: activity.instrument,
        });
      } else {
        saved = await backend.bulkSample.updateCompounding(activityId, {
          activityId,
          activity: updatedActivity,
          technique: activity.technique,
          instrument: activity.instrument,
        });
      }

      showSnackbar('Recipe saved successfully', 'success');
      setSaving(false);
      closeBulkRecipeEditorDialog(saved);
    } catch (err: any) {
      log.error('[BulkRecipeEditorDialog] Error saving recipe:', err);
      showSnackbar(getProblemDetail(err) ?? 'Failed to save recipe', 'error');
      setSaving(false);
    }
  }, [
    activity,
    activityId,
    activityType,
    recipeEditor,
    backend,
    showSnackbar,
    closeBulkRecipeEditorDialog,
    showMaterialSection,
    selectedMaterialId,
    showRuntimeField,
    runtime,
  ]);

  const hasUnsavedChanges = useMemo(() => {
    const recipeChanged =
      recipeEditor.recipeStore &&
      initialRecipeState &&
      JSON.stringify(recipeEditor.recipeStore.toState()) !== JSON.stringify(initialRecipeState);
    const materialChanged = showMaterialSection && selectedMaterialId !== initialMaterialId;
    const runtimeChanged = showRuntimeField && runtime !== initialRuntime;
    const runtimeManualFlagChanged =
      showRuntimeField &&
      recipeEditor.recipeStore &&
      recipeEditor.recipeStore.runtimeChangedManually !== (initialRecipeState?.runtimeChangedManually ?? false);
    return Boolean(recipeChanged || materialChanged || runtimeChanged || runtimeManualFlagChanged);
  }, [
    recipeEditor.recipeStore,
    initialRecipeState,
    showMaterialSection,
    selectedMaterialId,
    initialMaterialId,
    showRuntimeField,
    runtime,
    initialRuntime,
  ]);

  const handleUpdateTemplate = useCallback(async () => {
    if (!activity.recipe?.templateName) return;

    setUpdatingTemplate(true);
    try {
      const result = await recipeEditor.applyLatestTemplateUpdate({
        activityType: activity.type,
        technique: activity.technique,
        sampleTechnique: activity.sample?.techniqueId,
        instrument: activity.instrument || undefined,
        templateName: activity.recipe.templateName,
        currentData: recipeEditor.recipeStore?.toState() ?? recipeEditor.recipeData,
      });

      if (!result.success || !result.mergedRecipe) {
        showSnackbar('Template not found', 'error');
        return;
      }
      setInitialRecipeState(result.mergedRecipe);
      setTemplateOutdated(false);
      showSnackbar('Recipe updated to latest template', 'success');
    } catch (err: any) {
      log.error('[BulkRecipeEditorDialog] Failed to update template:', err);
      showSnackbar('Failed to update template', 'error');
    } finally {
      setUpdatingTemplate(false);
    }
  }, [activity, recipeEditor, showSnackbar]);

  return (
    <>
      <StandardDialog
        open
        onClose={onClose}
        title={dialogTitle}
        onSubmit={handleSave}
        submitText="Save"
        submitDisabled={recipeEditor.isReadOnly || !recipeEditor.recipeStore || saving}
        isLoading={saving}
        maxWidth="lg"
        hasUnsavedChanges={hasUnsavedChanges}
        unsavedChangesMessage="You have unsaved changes to the recipe. Are you sure you want to discard them?"
      >
        {recipeEditor.isInitializingRecipeStore || recipeEditor.loadingTemplates ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {activityInfoLine}
            </Typography>

            {showRuntimeField && !recipeEditor.isReadOnly ? (
              <RecipeActivityRuntimeField
                value={runtime}
                onInputChange={runtimeField.onInputChange}
                showResetButton={runtimeField.showResetButton}
                onResetToRecipe={runtimeField.onResetToRecipe}
              />
            ) : null}

            {showMaterialSection && !recipeEditor.isReadOnly ? (
              <Box sx={{ mb: 2, maxWidth: 480 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Target material
                </Typography>
                <Box display="flex" gap={1}>
                  <Autocomplete
                    options={materialOptions}
                    getOptionLabel={(option) => option.name}
                    filterOptions={(opts, params) => filterAutocompleteOptionsByMatchRank(opts, params, (o) => o.name)}
                    value={selectedMaterialOption}
                    onChange={(_, newValue) => {
                      setSelectedMaterialId(
                        newValue != null
                          ? typeof newValue.id === 'number'
                            ? newValue.id
                            : Number(newValue.id)
                          : undefined
                      );
                    }}
                    renderInput={(params) => <TextField {...params} size="small" placeholder="Select a material" />}
                    sx={{ flex: 1 }}
                  />
                  {canCreateMaterial ? <MaterialCreateAddButton onClick={() => setIsMaterialDialogOpen(true)} /> : null}
                </Box>
              </Box>
            ) : null}

            {!recipeEditor.isReadOnly && templateOutdated ? (
              <Alert
                severity="info"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleUpdateTemplate}
                    disabled={updatingTemplate}
                    startIcon={
                      updatingTemplate ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <i className="fas fa-redo"></i>
                      )
                    }
                  >
                    {updatingTemplate ? 'Updating…' : 'Update Template'}
                  </Button>
                }
                sx={{ mb: 2 }}
              >
                The recipe template has been updated. Click &quot;Update Template&quot; to use the latest version.
              </Alert>
            ) : null}

            {recipeEditor.recipeTemplateDTO && recipeEditor.recipeData ? (
              <Box sx={{ mt: 2, width: '100%' }}>
                {recipeEditor.recipeStore && recipeEditor.fullRecipeTemplate ? (
                  <Recipe store={recipeEditor.recipeStore} readonly={false} />
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                    <CircularProgress />
                  </Box>
                )}
              </Box>
            ) : !recipeEditor.recipeTemplateDTO ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                {recipeEditor.loadingTemplates ? (
                  <>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      Loading recipe templates...
                    </Typography>
                  </>
                ) : (
                  <>
                    {!recipeEditor.canEdit && activity.recipe ? (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Recipe template is unavailable. Editing is disabled to prevent data corruption.
                      </Alert>
                    ) : null}
                    <Typography variant="body1" color="text.secondary">
                      Select recipe template first
                    </Typography>
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No recipe found. Recipe will be created when you start editing.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </StandardDialog>

      <MaterialCreateDialog
        open={isMaterialDialogOpen}
        onClose={() => setIsMaterialDialogOpen(false)}
        onCreated={(savedMaterial, _freshMaterials) => {
          if (savedMaterial.id != null) {
            setSelectedMaterialId(savedMaterial.id);
          }
          showSnackbar('New material created successfully', 'success');
        }}
      />
    </>
  );
};

const BulkRecipeEditorDialogInner: FC<BulkRecipeEditorDialogInnerProps> = ({
  activity,
  activityId,
  activityType,
  startingMaterialNumber,
  startingMaterialSampleLabel,
  viewingSampleLabel,
  preparationOwnerRoles,
  forceReadOnly,
  initialMode,
  closeBulkRecipeEditorDialog,
}) => {
  const [mode, setMode] = useState<'view' | 'edit'>(() => initialMode || 'view');
  const preparationOwnerLabel = startingMaterialSampleLabel || activity.sample?.sampleLabel;
  const activityInfoLine = useActivityLabels(activity, {
    forceReadOnly,
    viewingSampleLabel,
    preparationOwnerLabel,
  });

  const handleClose = useCallback(() => {
    closeBulkRecipeEditorDialog(null);
  }, [closeBulkRecipeEditorDialog]);

  const dialogTitle = useMemo(
    () => getBulkRecipeDialogTitle(activityType, mode, startingMaterialNumber),
    [activityType, startingMaterialNumber, mode]
  );

  if (mode === 'view') {
    return (
      <BulkRecipeViewPanel
        activity={activity}
        activityType={activityType}
        activityInfoLine={activityInfoLine}
        startingMaterialNumber={startingMaterialNumber}
        startingMaterialSampleLabel={startingMaterialSampleLabel}
        viewingSampleLabel={viewingSampleLabel}
        preparationOwnerRoles={preparationOwnerRoles}
        forceReadOnly={forceReadOnly}
        dialogTitle={dialogTitle}
        onEdit={() => setMode('edit')}
        onClose={handleClose}
      />
    );
  }

  return (
    <BulkRecipeEditPanel
      key={`edit-${activityId}-${activityType}`}
      activity={activity}
      activityId={activityId}
      activityType={activityType}
      activityInfoLine={activityInfoLine}
      startingMaterialNumber={startingMaterialNumber}
      forceReadOnly={forceReadOnly}
      dialogTitle={dialogTitle}
      onClose={handleClose}
      closeBulkRecipeEditorDialog={closeBulkRecipeEditorDialog}
    />
  );
};

type BulkRecipeEditorDialogShellProps = {
  activityId: number;
  activityType: BulkRecipeActivityType;
  startingMaterialNumber?: number;
  startingMaterialSampleLabel?: string;
  viewingSampleLabel?: string;
  preparationOwnerRoles?: number[];
  forceReadOnly?: boolean;
  mode: 'view' | 'edit';
  closeBulkRecipeEditorDialog: (result?: InstrumentRunDto | SampleActivityDto | BulkSampleDto | null) => void;
};

const BulkRecipeEditorDialogShell: FC<BulkRecipeEditorDialogShellProps> = ({
  activityId,
  activityType,
  startingMaterialNumber,
  startingMaterialSampleLabel,
  viewingSampleLabel,
  preparationOwnerRoles,
  forceReadOnly,
  mode,
  closeBulkRecipeEditorDialog,
}) => {
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();

  const [activity, setActivity] = useState<SampleActivityDto | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const loadedActivity = await loadBulkRecipeActivity(backend, activityId, activityType);
        if (cancelled) return;

        if (!loadedActivity) {
          setError('Activity not found');
          return;
        }

        setActivity(loadedActivity);
      } catch (err: any) {
        if (cancelled) return;
        log.error('[BulkRecipeEditorDialog] Error loading activity:', err);
        setError(getProblemDetail(err) ?? 'Failed to load activity');
        showSnackbar('Failed to load recipe', 'error');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activityId, activityType, backend, showSnackbar]);

  const handleClose = useCallback(() => {
    closeBulkRecipeEditorDialog(null);
  }, [closeBulkRecipeEditorDialog]);

  const shellTitle = getBulkRecipeDialogTitle(activityType, mode, startingMaterialNumber);

  if (loading) {
    return (
      <StandardDialog
        open
        onClose={handleClose}
        title={shellTitle}
        maxWidth="lg"
        actions={<Button onClick={handleClose}>Close</Button>}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '200px', pt: 4 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading recipe...
          </Typography>
        </Box>
      </StandardDialog>
    );
  }

  if (error || !activity) {
    return (
      <StandardDialog
        open
        onClose={handleClose}
        title={shellTitle}
        maxWidth="lg"
        actions={<Button onClick={handleClose}>Close</Button>}
      >
        <Alert severity={error ? 'error' : 'warning'}>{error ?? 'Activity not found'}</Alert>
      </StandardDialog>
    );
  }

  return (
    <BulkRecipeEditorDialogInner
      activity={activity}
      activityId={activityId}
      activityType={activityType}
      startingMaterialNumber={startingMaterialNumber}
      startingMaterialSampleLabel={startingMaterialSampleLabel}
      viewingSampleLabel={viewingSampleLabel}
      preparationOwnerRoles={preparationOwnerRoles}
      forceReadOnly={forceReadOnly}
      initialMode={mode}
      closeBulkRecipeEditorDialog={closeBulkRecipeEditorDialog}
    />
  );
};

export const BulkRecipeEditorDialog: FC = () => {
  const { bulkRecipeEditorDialog, closeBulkRecipeEditorDialog } = useDialogStore();

  const dialogState = bulkRecipeEditorDialog ?? {
    isOpen: false,
    activityId: null,
    activityType: null,
    startingMaterialNumber: undefined,
    startingMaterialSampleLabel: undefined,
    viewingSampleLabel: undefined,
    preparationOwnerRoles: undefined,
    forceReadOnly: undefined,
    mode: 'view' as const,
  };
  const {
    isOpen,
    activityId,
    activityType,
    startingMaterialNumber,
    startingMaterialSampleLabel,
    viewingSampleLabel,
    preparationOwnerRoles,
    forceReadOnly,
    mode,
  } = dialogState;

  if (!isOpen || activityId == null || !activityType) {
    return null;
  }

  return (
    <BulkRecipeEditorDialogShell
      key={`${activityId}-${activityType}`}
      activityId={activityId}
      activityType={activityType}
      startingMaterialNumber={startingMaterialNumber}
      startingMaterialSampleLabel={startingMaterialSampleLabel}
      viewingSampleLabel={viewingSampleLabel}
      preparationOwnerRoles={preparationOwnerRoles}
      forceReadOnly={forceReadOnly}
      mode={mode || 'view'}
      closeBulkRecipeEditorDialog={closeBulkRecipeEditorDialog}
    />
  );
};
